import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  BarChart3, CheckCircle, Clock, XCircle, TrendingUp,
  List, ChevronRight, ChevronLeft, Loader2
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { getJobCustomer, getDisplayStatus, statusColors, statusLabels } from '../../utils/reportHelpers';
import KPIMetricCard from './KPIMetricCard';
import ReportPrintHeader from './ReportPrintHeader';

export default function JobTrackerReport({
  jobs, loading, kpiData, trackerPage, setTrackerPage, trackerPageSize,
  reportTitle, startDate, endDate, region,
  onDrilldownTotalJobs, onDrilldownCompleted, onDrilldownScheduled,
  onDrilldownCancelled, onDrilldownWithin1Day
}) {
  // Filter out leave events for Job Tracker display
  const clientJobs = jobs.filter(job => !job.event_type?.startsWith('LEAVE'));
  const totalPages = Math.ceil(clientJobs.length / trackerPageSize);
  const paginatedJobs = clientJobs.slice(trackerPage * trackerPageSize, (trackerPage + 1) * trackerPageSize);

  return (
    <div className="space-y-6 print-section" data-testid="job-tracker">
      <ReportPrintHeader title={reportTitle} startDate={startDate} endDate={endDate} region={region} />

      {/* KPI Summary */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 print:grid-cols-6">
        <KPIMetricCard
          icon={BarChart3}
          title="Total Jobs"
          description="Click to view all"
          value={kpiData?.total_jobs ?? '\u2014'}
          onClick={onDrilldownTotalJobs}
          testId="kpi-total-jobs"
        />
        <KPIMetricCard
          icon={CheckCircle}
          title="Completed"
          description="Successfully finished"
          value={kpiData?.completed_jobs ?? '\u2014'}
          onClick={onDrilldownCompleted}
          testId="kpi-completed"
          iconBgClass="bg-green-100"
          iconClass="text-green-600"
        />
        <KPIMetricCard
          icon={Clock}
          title="Scheduled"
          description="Pending execution"
          value={kpiData?.scheduled_jobs ?? '\u2014'}
          onClick={onDrilldownScheduled}
          testId="kpi-scheduled"
          iconBgClass="bg-blue-100"
          iconClass="text-blue-600"
        />
        <KPIMetricCard
          icon={XCircle}
          title="Cancelled"
          description="Jobs cancelled"
          value={kpiData?.cancelled_jobs ?? '\u2014'}
          onClick={onDrilldownCancelled}
          testId="kpi-cancelled"
          iconBgClass="bg-red-100"
          iconClass="text-red-600"
        />
        <KPIMetricCard
          icon={TrendingUp}
          title="Avg Days"
          description="Average completion time"
          value={kpiData?.avg_days_to_complete != null ? `${kpiData.avg_days_to_complete} days` : '\u2014'}
          testId="kpi-avg-days"
          iconBgClass="bg-purple-100"
          iconClass="text-purple-600"
        />
        <KPIMetricCard
          icon={CheckCircle}
          title="Within 1 Day"
          description="Quick completions"
          value={kpiData?.pct_completed_within_1_day != null ? `${kpiData.pct_completed_within_1_day}%` : '0%'}
          onClick={onDrilldownWithin1Day}
          testId="kpi-within-1-day"
          iconBgClass="bg-amber-100"
          iconClass="text-amber-600"
        />
      </div>

      {/* Job List Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <List className="h-5 w-5" />
            Job Details
          </CardTitle>
          <CardDescription>
            Line-by-line breakdown of all jobs in the selected period (excludes leave/holidays)
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>ID</TableHead>
                    <TableHead>Scheduled Date</TableHead>
                    <TableHead>Completion Date</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Site</TableHead>
                    <TableHead>Service</TableHead>
                    <TableHead>Auditor(s)</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedJobs.map(job => {
                    const customer = getJobCustomer(job);
                    const scheduledDateDisplay = job.end_date && job.end_date !== job.scheduled_date
                      ? `${format(parseISO(job.scheduled_date), 'd MMM yyyy')} - ${format(parseISO(job.end_date), 'd MMM yyyy')}`
                      : format(parseISO(job.scheduled_date), 'd MMM yyyy');
                    const completionDate = job.completed_on || job.completed_at;
                    const completionDateDisplay = job.status === 'completed' && completionDate
                      ? format(parseISO(completionDate), 'd MMM yyyy')
                      : '\u2014';
                    return (
                      <TableRow key={job.id} data-testid={`job-row-${job.id}`}>
                        <TableCell className="font-mono text-sm">{job.id}</TableCell>
                        <TableCell className="whitespace-nowrap">{scheduledDateDisplay}</TableCell>
                        <TableCell>{completionDateDisplay}</TableCell>
                        <TableCell>
                          <Badge className={statusColors[getDisplayStatus(job)] || 'bg-gray-100'}>
                            {statusLabels[getDisplayStatus(job)] || job.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {customer?.colour && (
                              <div
                                className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                                style={{ backgroundColor: customer.colour }}
                              />
                            )}
                            <span>{customer?.name || '\u2014'}</span>
                          </div>
                        </TableCell>
                        <TableCell>{job.site?.name || '\u2014'}</TableCell>
                        <TableCell className="truncate max-w-[100px]">{job.service?.name || '\u2014'}</TableCell>
                        <TableCell className="truncate max-w-[150px]">
                          {job.auditors?.map(a => `${a.first_name} ${a.last_name[0]}.`).join(', ') || '\u2014'}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {clientJobs.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                        No jobs found for the selected period
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          )}
          {/* Pagination */}
          {clientJobs.length > trackerPageSize && (
            <div className="flex items-center justify-between mt-4 print:hidden">
              <p className="text-sm text-muted-foreground">
                Showing {trackerPage * trackerPageSize + 1}-{Math.min((trackerPage + 1) * trackerPageSize, clientJobs.length)} of {clientJobs.length} jobs
              </p>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm"
                  onClick={() => setTrackerPage(p => Math.max(0, p - 1))}
                  disabled={trackerPage === 0}
                  data-testid="tracker-prev-page">
                  <ChevronLeft className="h-4 w-4 mr-1" /> Previous
                </Button>
                <span className="text-sm text-muted-foreground">
                  Page {trackerPage + 1} of {totalPages}
                </span>
                <Button variant="outline" size="sm"
                  onClick={() => setTrackerPage(p => Math.min(totalPages - 1, p + 1))}
                  disabled={trackerPage >= totalPages - 1}
                  data-testid="tracker-next-page">
                  Next <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
